import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from '../components';
import { api } from '../services';
import '../styles/AddUser.scss';
import '../styles/TemplateUpload.scss';

const MAX_BYTES = 20 * 1024 * 1024;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA') : 'Not uploaded yet');
const formatSize = (bytes) => (bytes == null ? '' : `${(bytes / 1024).toFixed(0)} KB`);

const TemplateUpload = () => {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInput = useRef(null);

  const load = () =>
    api.get('/core/admin/templates/')
      .then((res) => setTemplates(res.data.templates))
      .catch(() => setError('Could not load templates. You may not have admin access.'));

  useEffect(() => { load(); }, []);

  const handleFile = (e) => {
    setError('');
    setSuccess('');
    const picked = e.target.files[0] || null;
    if (picked && !picked.name.toLowerCase().endsWith('.xlsx')) {
      setError('Choose an .xlsx workbook.');
      setFile(null);
      return;
    }
    if (picked && picked.size > MAX_BYTES) {
      setError('File must be 20 MB or smaller.');
      setFile(null);
      return;
    }
    setFile(picked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!name || !file) {
      setError('Choose a template and a file.');
      return;
    }
    const body = new FormData();
    body.append('name', name);
    body.append('file', file);
    setIsLoading(true);
    try {
      const res = await api.post('/core/admin/templates/upload/', body);
      const label = res.data.template.display_name;
      setSuccess(res.data.changed ? `Uploaded. "${label}" is now live.` : `No change: "${label}" already has this file.`);
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-user-container template-upload-container">
      <Card title="Data Sheet Templates" className="add-user-card template-upload-card">
        <form onSubmit={handleSubmit}>
          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner">{success}</div>}
          <div className="add-user-field">
            <label className="input-label">Template</label>
            <select value={name} onChange={(e) => { setName(e.target.value); setSuccess(''); }} className="ci-select" required>
              <option value="">-- Select a template --</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>{t.display_name.replace(/\.xlsx$/, '')}</option>
              ))}
            </select>
          </div>
          <div className="add-user-field">
            <label className="input-label">Workbook (.xlsx, 20 MB maximum)</label>
            <input ref={fileInput} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} />
          </div>
          <Button type="submit" fullWidth disabled={isLoading || !name || !file}>
            {isLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </form>

        <table className="template-table">
          <thead>
            <tr><th>Template</th><th>Last updated</th><th>By</th><th>Size</th></tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.name}>
                <td>{t.display_name.replace(/\.xlsx$/, '')}</td>
                <td>{formatDate(t.updated_at)}</td>
                <td>{t.updated_by || ''}</td>
                <td>{formatSize(t.size)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default TemplateUpload;
